import {
  DailyEventObject,
  DailyEventObjectDialOutError,
  DailyEventObjectDialOutStopped,
} from '@daily-co/daily-js';
import { useDaily, useDailyEvent } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { useCallback, useRef, useState } from 'react';

export interface UseDialOutReturn {
  /** Initiate a dial-out call to the given phone number */
  startDialOut: (
    phoneNumber: string,
    displayName?: string,
  ) => Promise<string | null>;
  /**
   * Stop an active dial-out call. Resolves to false when Daily rejected the
   * request, which means the phone leg may still be connected even though the
   * hook no longer tracks it.
   */
  stopDialOut: () => Promise<boolean>;
  /** The current dial-out session ID, or null if no active call */
  dialOutSessionId: string | null;
  /** Whether a dial-out is currently in progress */
  isDialOutActive: boolean;
  /** Whether the dial-out is currently connecting */
  isConnecting: boolean;
  /** Error message if dial-out failed */
  error: string | null;
  /** Clear the current error */
  clearError: () => void;
}

/**
 * Daily's rejection messages can quote the number that was dialled, and phone
 * numbers are PHI — which is why the rest of this hook only ever logs
 * `phoneNumberMasked`. Redact anything that looks like a phone number before
 * the message reaches Datadog or the UI.
 */
const maskPhoneNumbers = (text: string): string =>
  text.replace(/\+?\d[\d\s().-]{5,}\d/g, (match) =>
    (match.match(/\d/g)?.length ?? 0) >= 7 ? '[phone redacted]' : match,
  );

/**
 * Daily rejects dial-out requests with plain objects rather than Errors, so
 * `err.message` is undefined and only these shapes carry a reason. Never log
 * the payload itself: it carries the dialled number and the room token.
 */
const dialOutErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error) {
    return maskPhoneNumbers(err.message);
  }
  if (typeof err === 'string') {
    return maskPhoneNumbers(err);
  }
  if (err && typeof err === 'object') {
    const { errorMsg, msg, error } = err as Record<string, unknown>;
    const message = [errorMsg, msg, error].find(
      (candidate) => typeof candidate === 'string',
    );
    if (typeof message === 'string') {
      return maskPhoneNumbers(message);
    }
  }
  return fallback;
};

/**
 * Hook for managing PSTN dial-out calls via Daily.co.
 *
 * Allows providers to call patients' phones directly from video calls
 * when there are technical difficulties on the patient's end.
 *
 * Requirements:
 * - The caller must have owner/admin permissions (is_owner: true in token)
 * - The room must have enable_dialout: true
 * - A phone number must be purchased on the Daily account
 *
 * @example
 * ```tsx
 * const { startDialOut, stopDialOut, isDialOutActive } = useDialOut();
 *
 * // Start a call
 * const sessionId = await startDialOut('+14151234567', 'Patient Name');
 *
 * // End the call
 * await stopDialOut();
 * ```
 */
export const useDialOut = (roomId?: string): UseDialOutReturn => {
  const daily = useDaily();
  const [dialOutSessionId, setDialOutSessionId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * `useDailyEvent` re-subscribes whenever its callback changes, so the event
   * handlers below read the tracked id from a ref rather than from state: that
   * keeps them stable and closes the window where an event lands on a handler
   * still holding the previous id.
   */
  const sessionIdRef = useRef<string | null>(null);
  const answeredDialOutIdsRef = useRef(new Set<string>());
  const startedDialOutRef = useRef<{
    sessionId: string;
    startedAtMs: number;
  } | null>(null);

  const trackSession = useCallback((sessionId: string | null) => {
    sessionIdRef.current = sessionId;
    setDialOutSessionId(sessionId);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const durationSecFor = useCallback((sessionId: string): number | null => {
    const started = startedDialOutRef.current;
    if (!started || started.sessionId !== sessionId) return null;
    return Math.floor((Date.now() - started.startedAtMs) / 1000);
  }, []);

  const startDialOut = useCallback(
    async (
      phoneNumber: string,
      displayName?: string,
    ): Promise<string | null> => {
      if (!daily) {
        const errorMsg = 'Call object not available';
        setError(errorMsg);
        datadogLogs.logger.error(
          'Call object not available',
          callLogContext('dial-out', {
            roomId: roomId ?? '',
            participantType: 'provider',
          }),
        );
        return null;
      }

      setIsConnecting(true);
      setError(null);

      try {
        const result = await daily.startDialOut({
          phoneNumber,
          ...(displayName && { displayName }),
        });
        const sessionId = result.session?.sessionId ?? null;
        startedDialOutRef.current = sessionId
          ? { sessionId, startedAtMs: Date.now() }
          : null;
        trackSession(sessionId);
        setIsConnecting(false);

        datadogLogs.logger.info(
          'Dial-out started',
          callLogContext('dial-out', {
            roomId: roomId ?? '',
            participantType: 'provider',
            phoneNumberMasked: phoneNumber.replace(/.(?=.{4})/g, '*'),
            sessionId,
          }),
        );

        return sessionId;
      } catch (err) {
        const errorMessage = dialOutErrorMessage(
          err,
          'Failed to start dial-out',
        );
        setError(errorMessage);
        setIsConnecting(false);

        datadogLogs.logger.error(
          'Dial-out failed',
          callLogContext('dial-out', {
            roomId: roomId ?? '',
            participantType: 'provider',
            error: errorMessage,
            phoneNumberMasked: phoneNumber.replace(/.(?=.{4})/g, '*'),
          }),
          err instanceof Error ? err : undefined,
        );

        return null;
      }
    },
    [daily, roomId, trackSession],
  );

  const stopDialOut = useCallback(async (): Promise<boolean> => {
    if (!daily || !dialOutSessionId) {
      return true;
    }

    // Drop the session before awaiting Daily: retrying a rejected stop with the
    // same id never succeeds, and holding on to it keeps isDialOutActive true,
    // wedging the tray button in "hang up" mode.
    trackSession(null);

    try {
      await daily.stopDialOut({ sessionId: dialOutSessionId });

      datadogLogs.logger.info(
        'Dial-out stopped',
        callLogContext('dial-out', {
          roomId: roomId ?? '',
          participantType: 'provider',
          sessionId: dialOutSessionId,
          answerEventObserved:
            answeredDialOutIdsRef.current.has(dialOutSessionId),
          durationSec: durationSecFor(dialOutSessionId),
        }),
      );

      return true;
    } catch (err) {
      const errorMessage = dialOutErrorMessage(err, 'Failed to stop dial-out');
      setError(errorMessage);

      datadogLogs.logger.error(
        'Stop dial-out failed',
        callLogContext('dial-out', {
          roomId: roomId ?? '',
          participantType: 'provider',
          detail:
            'Daily rejected the hang-up; the phone leg may still be connected',
          error: errorMessage,
          sessionId: dialOutSessionId,
        }),
      );

      return false;
    }
  }, [daily, dialOutSessionId, durationSecFor, roomId, trackSession]);

  const handleDialOutAnswered = useCallback(
    (event: DailyEventObject<'dialout-answered'>) => {
      const sessionId = 'sessionId' in event ? event.sessionId : undefined;
      if (typeof sessionId !== 'string' || !sessionId) return;
      if (answeredDialOutIdsRef.current.has(sessionId)) return;
      answeredDialOutIdsRef.current.add(sessionId);

      datadogLogs.logger.info(
        'Dial-out answered',
        callLogContext('dial-out', {
          roomId: roomId ?? '',
          participantType: 'provider',
          sessionId,
          isTrackedSession: sessionId === sessionIdRef.current,
        }),
      );
    },
    [roomId],
  );

  /**
   * Events without a `sessionId` still count: only one dial-out can be in
   * flight per call, so a dial-out that ended is necessarily ours.
   *
   * @returns the id we stopped tracking, or null if the event was not ours.
   */
  const forgetSession = useCallback(
    (eventSessionId?: string): string | null => {
      const tracked = sessionIdRef.current;

      if (!tracked || (eventSessionId && eventSessionId !== tracked)) {
        return null;
      }

      trackSession(null);
      return tracked;
    },
    [trackSession],
  );

  /**
   * The phone leg can end without the provider touching the tray — the patient
   * hangs up, or the carrier drops the call — and Daily only announces it here.
   */
  const handleDialOutStopped = useCallback(
    (event: DailyEventObjectDialOutStopped) => {
      const endedSessionId = forgetSession(event?.sessionId);

      if (!endedSessionId) {
        return;
      }

      datadogLogs.logger.info(
        'Dial-out ended remotely',
        callLogContext('dial-out', {
          roomId: roomId ?? '',
          participantType: 'provider',
          detail: 'The phone leg ended without the provider pressing hang up',
          sessionId: endedSessionId,
          answerEventObserved:
            answeredDialOutIdsRef.current.has(endedSessionId),
          durationSec: durationSecFor(endedSessionId),
        }),
      );
    },
    [durationSecFor, forgetSession, roomId],
  );

  /**
   * Fatal failure in Daily's dial-out pipeline — the leg is gone either way.
   * `errorMsg` can quote the dialled number and `details` carries it outright,
   * so mask the message and never log the event itself.
   */
  const handleDialOutError = useCallback(
    (event: DailyEventObjectDialOutError) => {
      const eventSessionId = event?.sessionId || undefined;
      const errorMessage = maskPhoneNumbers(
        event?.errorMsg ?? 'The phone call ended unexpectedly',
      );

      if (eventSessionId && eventSessionId !== sessionIdRef.current) {
        datadogLogs.logger.warn(
          'Dial-out error for untracked session',
          callLogContext('dial-out', {
            roomId: roomId ?? '',
            participantType: 'provider',
            error: errorMessage,
            sessionId: eventSessionId,
            isTrackedSession: false,
            answerEventObserved:
              answeredDialOutIdsRef.current.has(eventSessionId),
          }),
        );
        return;
      }

      // Without a tracked session this is Daily echoing a startDialOut
      // rejection, which the startDialOut catch already logs and surfaces.
      const endedSessionId = forgetSession(eventSessionId);
      if (!endedSessionId) return;

      setError(errorMessage);

      datadogLogs.logger.error(
        'Dial-out failed after connecting',
        callLogContext('dial-out', {
          roomId: roomId ?? '',
          participantType: 'provider',
          error: errorMessage,
          sessionId: endedSessionId,
          answerEventObserved:
            answeredDialOutIdsRef.current.has(endedSessionId),
          durationSec: durationSecFor(endedSessionId),
        }),
      );
    },
    [durationSecFor, forgetSession, roomId],
  );

  useDailyEvent('dialout-answered', handleDialOutAnswered);
  useDailyEvent('dialout-stopped', handleDialOutStopped);
  useDailyEvent('dialout-error', handleDialOutError);

  return {
    startDialOut,
    stopDialOut,
    dialOutSessionId,
    isDialOutActive: dialOutSessionId !== null,
    isConnecting,
    error,
    clearError,
  };
};
